import React, { useEffect, useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { Navigation } from "swiper/modules";
import { NewsCard } from "./news-card";
import ItemSkeleton from "../../../components/base/ItemSkeleton";
import { Box, Divider, Stack, Typography } from "@mui/material";
import "assets/styles/slider.css";
import { useSelector } from "react-redux";
import { useIsMobile } from "hooks/use-breakpoint-checks.hook";
import BoclipsCard from "./boclips-card";
import { boClipsLabel } from "features/SourceSearch/data/search-fields.data";
import { useLocation, useParams } from "react-router-dom";
import { getSourceConfig } from "config";

export default function SectionNews({
  category,
  query,
  sectionNews,
  loading,
  isLoading,
  onFetchMore,
}) {
  const isMobile = useIsMobile();

  const [newsCards, setNewsCards] = useState();
  const { bookmarkedData, noFetchMore } = useSelector((state) => state.data);

  const [swiper, setSwiper] = useState(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);
  const [hasFetchedMore, setHasFetchedMore] = useState(false);
  const shouldSlideRef = useRef(false);
  const { pathname } = useLocation();
  const { source } = useParams();

  useEffect(() => {
    if (category === "Videos" && bookmarkedData.id && newsCards?.length) {
      const lastId = bookmarkedData.id.split("/").pop();
      const bookmarkedNewsData = newsCards.map((item) => {
        if (item.id === lastId) {
          const itemData = {
            ...item,
            collectionSourceId: bookmarkedData.collectionSourceId,
            collectionId: bookmarkedData.collectionId,
            ...(bookmarkedData.collectionMemberships && {
              collectionMemberships: bookmarkedData.collectionMemberships,
            }),
            isCitationAvailable: bookmarkedData.isCitationAvailable,
            isSummaryAvailable: bookmarkedData.isSummaryAvailable,
          };
          return itemData;
        }
        return item;
      });
      setNewsCards(bookmarkedNewsData);
    } else if (bookmarkedData.id && newsCards?.length) {
      const bookmarkedNewsData = newsCards.map((item) => {
        if (item.url === bookmarkedData.id) {
          const itemData = {
            ...item,
            collectionSourceId: bookmarkedData.collectionSourceId,
            collectionId: bookmarkedData.collectionId,
            ...(bookmarkedData.collectionMemberships && {
              collectionMemberships: bookmarkedData.collectionMemberships,
            }),
            isCitationAvailable: bookmarkedData.isCitationAvailable,
            isSummaryAvailable: bookmarkedData.isSummaryAvailable,
          };
          return itemData;
        }
        return item;
      });
      setNewsCards(bookmarkedNewsData);
    }
  }, [
    bookmarkedData.collectionId,
    bookmarkedData.collectionSourceId,
    bookmarkedData.collectionMemberships,
    bookmarkedData.id,
  ]);

  useEffect(() => {
    if (swiper) {
      swiper.on("slideChange", () => {
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
      });
    }
  }, [swiper]);

  useEffect(() => {
    if (category == boClipsLabel) {
      setNewsCards(sectionNews);
    } else setNewsCards(sectionNews?.webPages?.value || []);

    if (fetchMoreLoading) {
      setFetchMoreLoading(false);
      shouldSlideRef.current = true;
    } else if (sectionNews) {
      setHasFetchedMore(false);
      if (swiper) {
        swiper.slideTo(0);
      }
    }
  }, [sectionNews]);

  useEffect(() => {
    if (noFetchMore[category] && fetchMoreLoading) {
      setFetchMoreLoading(false);
    }
  }, [noFetchMore, category, fetchMoreLoading]);

  useEffect(() => {
    if (shouldSlideRef.current && swiper) {
      shouldSlideRef.current = false;
      swiper.update();
      swiper.slideNext();
    }
  }, [newsCards, swiper]);

  useEffect(() => {
    setHasFetchedMore(false);
    if (swiper) {
      swiper.slideTo(0);
    }
  }, [pathname, category, query, swiper]);

  const isEmpty =
    !isLoading &&
    (category === boClipsLabel
      ? sectionNews?.length === 0
      : sectionNews?.webPages?.value?.length === 0 || !sectionNews?.webPages);

  const handleReachEnd = () => {
    if (
      !hasFetchedMore &&
      typeof onFetchMore === "function" &&
      category !== boClipsLabel &&
      getSourceConfig(source, true).count < getSourceConfig(source, true).maxResults
    ) {
      console.log("Fetching More");
      setFetchMoreLoading(true);
      onFetchMore(category);
      setHasFetchedMore(true);
    }
  };

  if (isEmpty) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          minHeight: { xs: "auto", sm: "18vh", lg: "30vh" },
        }}
      >
        <Typography>No results Found</Typography>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Stack spacing={2}>
        {isLoading && (
          <ItemSkeleton
            sx={{
              minHeight: "200px",
            }}
          />
        )}
        {!isLoading &&
          newsCards?.length &&
          newsCards?.slice(0, 3).map((newsCard, index) => (
            <React.Fragment key={newsCard.id || index}>
              {category === boClipsLabel ? (
                <BoclipsCard query={query} data={newsCard} />
              ) : (
                <NewsCard category={category} query={query} data={newsCard} />
              )}
              {index !== 2 && <Divider />}{" "}
            </React.Fragment>
          ))}
      </Stack>
    );
  }

  return (
    <Box
      className="tour-cat-section-slider"
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: { xs: "auto", sm: "18vh", lg: "30vh" },
      }}
    >
      <style jsx>{`
        .mySwiper.fetch-more-loading .swiper-button-next::after {
          content: "";
          width: 20px;
          height: 20px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #3498db;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          font-size: 0;
        }

        .mySwiper.fetch-more-loading .swiper-button-next {
          opacity: 1;
          cursor: default;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>

      <Swiper
        onSwiper={setSwiper}
        onReachEnd={handleReachEnd}
        slidesPerView={3}
        spaceBetween={30}
        slidesPerGroup={3}
        loop={false}
        pagination={{
          clickable: true,
        }}
        navigation={!loading}
        modules={[Navigation]}
        observer={true}
        observeParents={true}
        className={`mySwiper ${fetchMoreLoading && !noFetchMore[category] ? "fetch-more-loading" : ""}`}
        breakpoints={{
          320: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
          768: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
          1072: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
          1700: {
            slidesPerView: 4,
            slidesPerGroup: 4,
          },
          2000: {
            slidesPerView: 5,
            slidesPerGroup: 5,
          },
        }}
        watchSlidesProgress
      >
        {isLoading || !newsCards?.length
          ? Array.from(Array(5), (e, i) => {
            return (
              <SwiperSlide key={i}>
                <ItemSkeleton
                  sx={{
                    minHeight: { xs: "auto", sm: "18vh", lg: "30vh" },
                  }}
                />
              </SwiperSlide>
            );
          })
          : newsCards?.map((newsCard, index) => (
            <SwiperSlide
              key={newsCard.id || index}
              sx={{
                minHeight: { xs: "auto", sm: "18vh", lg: "30vh" }, // Consistent height for slides
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%", // Ensure content fills the slide height
                }}
              >
                {category === boClipsLabel ? (
                  <BoclipsCard data={newsCard} query={query} />
                ) : (
                  <NewsCard
                    category={category}
                    query={query}
                    data={newsCard}
                    slideIndex={index}
                  />
                )}
              </Box>
            </SwiperSlide>
          ))}
      </Swiper>
    </Box>
  );
}
